import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { lock, unlock } from '@/lib/bodyScrollLock'
import { createPortal } from 'react-dom'
import { useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { Avatar } from '@/components/Avatar'
import { RichComposer } from '@/components/RichComposer'
import { usePost, type PostResult } from '@/hooks/usePost'
import { useAuth } from '@/hooks/useAuth'
import { useViewProfile } from '@/hooks/useViewProfile'
import { useProtocol } from '@/hooks/useProtocol'
import { useBskyDrafts } from '@/hooks/useBskyDrafts'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useToast } from '@/hooks/useToast'
import { useClickOutside } from '@/hooks/useClickOutside'
import { Image, X, Plus, ShieldAlert, Globe, Quote, Layers } from 'lucide-react'
import { VideoUploadProgress } from '@/components/VideoUploadProgress'
import { AltTextModal } from '@/components/AltTextModal'
import { LinkCardPreview } from '@/components/LinkCardPreview'
import { QuotedPostEmbed } from '@/components/QuotedPostEmbed'
import { MASTODON_PROPAGATION_DELAY_MS } from '@/constants/mastodon'
import { fetchEmbeddedPost } from '@/services/postsService'
import {
    DraftAlreadyDeletedError,
    DraftChangedElsewhereError,
    DraftTextTooLongError,
    isDraftLimitReachedError,
} from '@/services/bskyDraftsService'
import { strings } from '@/strings'
import type { Post, PostsPage, QuoteContext, ReplyContext } from '@/types/posts'
import type { Thread } from '@/types/thread'
import type { BskyDraft, BskyDraftRecordRef } from '@/types/drafts'
import { buildThreadReplyUpdater } from './buildThreadReplyUpdater'
import { lossReasons } from './droppedFeatureLabel'
import { formatRelativeTime } from '@/lib/time'

// tinyld resolves to its full node build (~246 KB gzip), so it loads on demand
// instead of riding in the main chunk. Memoised so it is fetched at most once; a failed
// load clears the memo so a later keystroke retries.
let tinyldModule: Promise<typeof import('tinyld')> | undefined
function loadTinyld() {
    tinyldModule ??= import('tinyld').catch(error => {
        tinyldModule = undefined
        throw error
    })
    return tinyldModule
}

/**
 * Hydration state for a quote restored from a Bluesky draft.
 *
 * `app.bsky.draft.defs#draftEmbedRecord` stores a bare strongRef `{uri, cid}` — no author, no
 * text, nothing renderable — so the quoted post has to be fetched before it can be previewed.
 * Publishing stays disabled until this settles: silently publishing what the user meant as a
 * quote post as a bare text post is a content change they never approved.
 */
type QuoteHydration =
    | { status: 'pending' }
    | { status: 'error' }
    /** The quoted record was edited after the draft was saved — its cid no longer matches. */
    | { status: 'conflict'; post: Post; ref: BskyDraftRecordRef }
    | null

/** Which mutation the "updated somewhere else" prompt is blocking. */
type StaleConflict = { action: 'save' | 'delete' } | null

const CW_LABELS = [
    { val: 'nudity', label: strings.compose.cwLabels.nudity },
    { val: 'sexual', label: strings.compose.cwLabels.sexualContent },
    { val: 'graphic-media', label: strings.compose.cwLabels.graphicMedia },
    { val: 'porn', label: strings.compose.cwLabels.explicit },
] as const

const DETECTION_LANGS = [
    'en',
    'es',
    'fr',
    'de',
    'pt',
    'ja',
    'ko',
    'zh',
    'it',
    'nl',
    'ru',
    'ar',
    'hi',
    'tr',
    'pl',
    'sv',
    'da',
    'fi',
    'no',
] as const

const COMMON_LANGS = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'zh', name: 'Chinese' },
    { code: 'it', name: 'Italian' },
    { code: 'nl', name: 'Dutch' },
    { code: 'ru', name: 'Russian' },
    { code: 'ar', name: 'Arabic' },
    { code: 'hi', name: 'Hindi' },
    { code: 'tr', name: 'Turkish' },
    { code: 'pl', name: 'Polish' },
    { code: 'sv', name: 'Swedish' },
    { code: 'da', name: 'Danish' },
    { code: 'fi', name: 'Finnish' },
    { code: 'no', name: 'Norwegian' },
] as const

type DraftMetadataTag = { key: string; Icon: typeof Quote; text: string }

/**
 * Small icon+text tags for a drafts-list row, mirroring the official app's lighter-weight
 * treatment. Presentation only — derived entirely from data `classifyDraft` already computed.
 */
function draftMetadataTags(draft: BskyDraft): DraftMetadataTag[] {
    const tags: DraftMetadataTag[] = []
    if (draft.recordRef) {
        tags.push({ key: 'quote', Icon: Quote, text: strings.drafts.tags.quote })
    }
    const additionalPosts = draft.classification.droppedFeatures.find(
        f => f.kind === 'additionalPosts'
    )
    if (additionalPosts) {
        tags.push({
            key: 'morePosts',
            Icon: Layers,
            text: strings.drafts.tags.morePosts(additionalPosts.count ?? 0),
        })
    }
    return tags
}

interface BaseComposeModalProps {
    isOpen: boolean
    onClose: () => void
    submitLabel?: string
    placeholder?: string
    replyTo?: ReplyContext | null
    quoteOf?: QuoteContext | null
    invalidateQueryKeys?: string[][]
    children?: ReactNode
}

export function BaseComposeModal({
    isOpen,
    onClose,
    submitLabel = strings.profile.postComposer.post,
    placeholder = strings.profile.postComposer.placeholder,
    replyTo,
    quoteOf,
    invalidateQueryKeys = [],
    children,
}: BaseComposeModalProps) {
    const queryClient = useQueryClient()
    const { session, mastodonSession, pixelfedSession } = useAuth()
    const { selectedProtocol } = useProtocol()
    const { profile } = useViewProfile(session?.did)
    // Declared before useBskyDrafts because it drives the list query's `enabled`.
    const [showDraftsList, setShowDraftsList] = useState(false)
    const {
        agent: draftsAgent,
        drafts,
        isLoading: isLoadingDrafts,
        isError: isDraftsError,
        hasNextPage: hasMoreDrafts,
        isFetchingNextPage: isFetchingMoreDrafts,
        fetchNextPage: fetchMoreDrafts,
        refetch: refetchDrafts,
        saveDraft,
        deleteDraft,
        cleanupPublishedDraft,
        isSaving,
    } = useBskyDrafts({ listOpen: showDraftsList })
    const { flags } = useFeatureFlags()
    // Bluesky-only by construction, and hidden for replies: app.bsky.draft has no reply
    // reference anywhere in the lexicon, and the official Bluesky app does not offer
    // save-as-draft for replies either (Composer.tsx `{!isReply && <DraftsButton/>}`).
    const isDraftsEnabled = !!flags.bskyDrafts && selectedProtocol === 'bluesky' && !replyTo
    const { showToast } = useToast()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isClosing, setIsClosing] = useState(false)
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
    /** Draft awaiting the import-a-copy loss confirmation. Cancelling is a complete no-op. */
    const [importCandidate, setImportCandidate] = useState<BskyDraft | null>(null)
    /** Draft awaiting delete confirmation. Cancelling is a complete no-op. */
    const [deleteCandidate, setDeleteCandidate] = useState<BskyDraft | null>(null)
    const [showAttachmentSaveConfirm, setShowAttachmentSaveConfirm] = useState(false)
    const [quoteHydration, setQuoteHydration] = useState<QuoteHydration>(null)
    const [staleConflict, setStaleConflict] = useState<StaleConflict>(null)
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640)
    const [keyboardHeight, setKeyboardHeight] = useState(0)
    const [editingAltTextId, setEditingAltTextId] = useState<string | null>(null)
    const [altTextDraft, setAltTextDraft] = useState('')
    // Quote context restored from a loaded draft. Only used when no quoteOf prop is set
    // (i.e., the modal was opened as a plain ComposeModal and the user picked a quote draft).
    const [loadedQuoteContext, setLoadedQuoteContext] = useState<QuoteContext | null>(null)
    const [selfLabel, setSelfLabel] = useState<string | undefined>(undefined)
    const [showCWMenu, setShowCWMenu] = useState(false)
    const [detectedLang, setDetectedLang] = useState<string | undefined>(undefined)
    const [userLang, setUserLang] = useState<string | undefined>(undefined)
    const [langCleared, setLangCleared] = useState(false)
    const [showLangMenu, setShowLangMenu] = useState(false)
    const prevTextLengthRef = useRef(0)
    const langClearedAtLengthRef = useRef(0)
    const cwMenuRef = useRef<HTMLDivElement>(null)
    const langMenuRef = useRef<HTMLDivElement>(null)
    const draftsHeadingRef = useRef<HTMLParagraphElement>(null)
    const draftsButtonRef = useRef<HTMLButtonElement>(null)
    const composerContainerRef = useRef<HTMLDivElement>(null)
    const isFirstDraftsViewRenderRef = useRef(true)
    const backButtonClickedRef = useRef(false)

    // The draft currently loaded into the composer, so we can update it on save and delete it
    // on publish. Under Lossless-Only Editing this is ALWAYS a Mackinac-created draft — an
    // imported copy leaves it null until the user explicitly saves — so the update and
    // delete-after-publish paths are structurally incapable of touching a source draft.
    const loadedDraftIdRef = useRef<string | null>(null)
    // `updatedAt` as it was when the user was last shown this draft, for the pre-mutation
    // freshness check. Captured at load time, never re-derived from a possibly-refreshed list.
    const loadedDraftUpdatedAtRef = useRef<string | null>(null)
    const onCloseRef = useRef(onClose)
    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    useLayoutEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 640)
        window.addEventListener('resize', handler)
        return () => window.removeEventListener('resize', handler)
    }, [])

    useLayoutEffect(() => {
        const vv = window.visualViewport
        if (!vv) return
        const handler = () => {
            const height = window.innerHeight - vv.height - vv.offsetTop
            setKeyboardHeight(Math.max(0, height))
        }
        vv.addEventListener('resize', handler)
        vv.addEventListener('scroll', handler)
        return () => {
            vv.removeEventListener('resize', handler)
            vv.removeEventListener('scroll', handler)
        }
    }, [])

    const performClose = useCallback(() => {
        if (isMobile) {
            setIsClosing(true)
            setTimeout(() => {
                onCloseRef.current()
            }, 250) // matches slide-down animation duration in index.css
        } else {
            onCloseRef.current()
        }
    }, [isMobile])

    const performCloseRef = useRef(performClose)
    useEffect(() => {
        performCloseRef.current = performClose
    }, [performClose])

    const isFediverse = selectedProtocol === 'mastodon' || selectedProtocol === 'pixelfed'
    const fediverseSession = selectedProtocol === 'pixelfed' ? pixelfedSession : mastodonSession
    const currentUserAvatar = isFediverse ? fediverseSession?.account?.avatar : profile?.avatar

    // Prefer the prop (e.g. opened from a "Quote" action) over the draft-restored context,
    // so opening QuoteModal then loading an unrelated draft doesn't change the quote target.
    const effectiveQuoteOf = quoteOf ?? loadedQuoteContext

    // Stable ref for the temp URI used by the optimistic insert so onSuccess can find it
    const tempUriRef = useRef<string | null>(null)
    // Tracks which ['posts', identifier] cache keys received the optimistic insert,
    // so onSuccess can scope the temp-URI replacement to the same entries.
    const tempAuthorIdsRef = useRef<string[]>([])

    const effectiveLang = langCleared ? undefined : (userLang ?? detectedLang)

    const closeCWMenu = useCallback(() => setShowCWMenu(false), [])
    const closeLangMenu = useCallback(() => setShowLangMenu(false), [])
    useClickOutside(cwMenuRef, closeCWMenu, showCWMenu)
    useClickOutside(langMenuRef, closeLangMenu, showLangMenu)

    // Move focus into the drafts view on open (its heading), since the swap removes the
    // just-clicked control from the DOM and would otherwise drop focus to <body>. Skips the
    // very first render so mounting the modal with showDraftsList already false doesn't steal
    // focus onto the Drafts button. Returning focus to the Drafts button only happens for the
    // actual Back navigation (flagged by backButtonClickedRef in the header's onClick) — loading
    // a draft into the composer also flips showDraftsList to false as a side effect, and in that
    // case focus should land in the composer instead (see loadDraftIntoComposer).
    useEffect(() => {
        if (isFirstDraftsViewRenderRef.current) {
            isFirstDraftsViewRenderRef.current = false
            return
        }
        if (showDraftsList) {
            draftsHeadingRef.current?.focus()
        } else if (backButtonClickedRef.current) {
            backButtonClickedRef.current = false
            draftsButtonRef.current?.focus()
        } else {
            composerContainerRef.current?.querySelector('textarea')?.focus()
        }
    }, [showDraftsList])

    const post = usePost({
        replyTo: replyTo ?? undefined,
        quoteOf: effectiveQuoteOf ?? undefined,
        selfLabel,
        langs: effectiveLang ? [effectiveLang] : undefined,
        onBeforeSubmit: (text: string) => {
            // Build optimistic post with a temporary URI and insert immediately
            const tempUri = `temp:optimistic:${Date.now()}`
            tempUriRef.current = tempUri

            const isAP = isFediverse
            let optimisticPost: Post | null = null

            if (!isAP && session) {
                optimisticPost = {
                    uri: tempUri,
                    cid: undefined,
                    text,
                    createdAt: new Date().toISOString(),
                    isReply: !!replyTo,
                    replyParentUri: replyTo?.parent.uri,
                    replyRootUri: replyTo?.root.uri,
                    replyRootCid: replyTo?.root.cid,
                    quotedPost: effectiveQuoteOf?.post ?? undefined,
                    metrics: { likeCount: 0, repostCount: 0, replyCount: 0 },
                    author: {
                        did: session.did,
                        handle: profile?.handle ?? '',
                        displayName: profile?.displayName,
                        avatarUrl: profile?.avatar,
                    },
                    isActivityPub: false,
                }
            } else if (isAP && fediverseSession) {
                const hostname = new URL(fediverseSession.instance_url).host
                optimisticPost = {
                    uri: tempUri,
                    cid: undefined,
                    text,
                    createdAt: new Date().toISOString(),
                    isReply: !!replyTo,
                    quotedPost: effectiveQuoteOf?.post ?? undefined,
                    metrics: { likeCount: 0, repostCount: 0, replyCount: 0 },
                    author: {
                        did: '',
                        handle: `${fediverseSession.account.username}@${hostname}`,
                        displayName: fediverseSession.account.display_name || undefined,
                        avatarUrl: fediverseSession.account.avatar || undefined,
                    },
                    likeUri: null,
                    repostUri: null,
                    isActivityPub: true,
                }
            }

            if (optimisticPost) {
                if (replyTo) {
                    const parentUri = replyTo.parent.uri
                    const updater = buildThreadReplyUpdater(parentUri, optimisticPost)
                    queryClient.setQueriesData<Thread>({ queryKey: ['thread'] }, updater)
                    if (optimisticPost.isActivityPub) {
                        queryClient.setQueriesData<Thread>({ queryKey: ['ap-thread'] }, updater)
                    }
                } else {
                    // Scope the optimistic insert to the current user's own profile feed
                    // so it doesn't appear on other users' cached profile feeds.
                    const insertOptimistic = (old: InfiniteData<PostsPage> | undefined) => {
                        if (!old?.pages?.length) return old
                        return {
                            ...old,
                            pages: [
                                {
                                    ...old.pages[0],
                                    posts: [optimisticPost, ...old.pages[0].posts],
                                },
                                ...old.pages.slice(1),
                            ],
                        }
                    }
                    const authorIds: string[] = []
                    if (optimisticPost.isActivityPub) {
                        if (optimisticPost.author?.handle) {
                            authorIds.push(optimisticPost.author.handle)
                            queryClient.setQueriesData<InfiniteData<PostsPage>>(
                                { queryKey: ['posts', optimisticPost.author.handle] },
                                insertOptimistic
                            )
                        }
                    } else {
                        if (optimisticPost.author?.did) {
                            authorIds.push(optimisticPost.author.did)
                            queryClient.setQueriesData<InfiniteData<PostsPage>>(
                                { queryKey: ['posts', optimisticPost.author.did] },
                                insertOptimistic
                            )
                        }
                        if (optimisticPost.author?.handle) {
                            authorIds.push(optimisticPost.author.handle)
                            queryClient.setQueriesData<InfiniteData<PostsPage>>(
                                { queryKey: ['posts', optimisticPost.author.handle] },
                                insertOptimistic
                            )
                        }
                    }
                    tempAuthorIdsRef.current = authorIds
                }

                // Close modal immediately — the reply is already visible
                performCloseRef.current()
            }
        },
        onSuccess: async (result?: PostResult) => {
            // Capture the loaded draft for cleanup. Cleanup runs at the END of this handler so
            // it can be awaited without blocking the post's own success reporting.
            const loadedDraftId = loadedDraftIdRef.current
            const loadedDraftUpdatedAt = loadedDraftUpdatedAtRef.current ?? undefined
            loadedDraftIdRef.current = null
            loadedDraftUpdatedAtRef.current = null

            const tempUri = tempUriRef.current
            tempUriRef.current = null
            const authorIds = tempAuthorIdsRef.current
            tempAuthorIdsRef.current = []

            // Replace the temp-URI optimistic post with real server data
            if (result && tempUri) {
                const replaceTempPost = (p: Post): Post =>
                    p.uri === tempUri
                        ? {
                              ...p,
                              uri: result.uri,
                              cid: result.cid,
                              rkey: result.uri.split('/').pop(),
                              mastodonStatusId: p.isActivityPub ? result.cid : undefined,
                          }
                        : p

                if (replyTo) {
                    const replaceInThread = (old: Thread | undefined) => {
                        if (!old) return old
                        return {
                            ...old,
                            replies: old.replies.map(r => ({
                                ...r,
                                post: replaceTempPost(r.post),
                            })),
                        }
                    }
                    queryClient.setQueriesData<Thread>({ queryKey: ['thread'] }, replaceInThread)
                    queryClient.setQueriesData<Thread>({ queryKey: ['ap-thread'] }, replaceInThread)
                } else {
                    // Scope the replacement to the same cache entries as the optimistic insert
                    for (const authorId of authorIds) {
                        queryClient.setQueriesData<InfiniteData<PostsPage>>(
                            { queryKey: ['posts', authorId] },
                            old => {
                                if (!old) return old
                                return {
                                    ...old,
                                    pages: old.pages.map(page => ({
                                        ...page,
                                        posts: page.posts.map(replaceTempPost),
                                    })),
                                }
                            }
                        )
                    }
                }
            }

            // For Bluesky: actively refetch so profile feed shows the new post immediately.
            // For Fediverse: a delayed active refetch is handled below; mark stale only here.
            void queryClient.invalidateQueries({
                queryKey: ['posts'],
                ...(isFediverse ? { refetchType: 'none' as const } : {}),
            })

            if (replyTo) {
                void queryClient.invalidateQueries({
                    queryKey: ['thread'],
                    refetchType: 'none',
                })
                void queryClient.invalidateQueries({
                    queryKey: ['ap-thread'],
                    refetchType: 'none',
                })
            } else {
                for (const key of invalidateQueryKeys) {
                    void queryClient.invalidateQueries({
                        queryKey: key,
                        refetchType: 'none',
                    })
                }
            }

            // Mastodon instances need time to propagate new posts.
            // Schedule an active refetch after a delay so the optimistic
            // post gets replaced by real server data rather than vanishing.
            if (isFediverse) {
                setTimeout(() => {
                    void queryClient.invalidateQueries({ queryKey: ['posts'] })
                    if (replyTo) {
                        void queryClient.invalidateQueries({ queryKey: ['thread'] })
                        void queryClient.invalidateQueries({ queryKey: ['ap-thread'] })
                    }
                }, MASTODON_PROPAGATION_DELAY_MS)
            }

            // Delete the draft the post was published from. Awaited (so a failure is observed
            // rather than silently dropped) and durable (the id is persisted before the attempt
            // and retried on the next drafts-hook mount), but deliberately last: a cleanup
            // failure is NEVER surfaced as a failed post.
            if (isDraftsEnabled && loadedDraftId) {
                const outcome = await cleanupPublishedDraft(loadedDraftId, loadedDraftUpdatedAt)
                if (outcome === 'skipped-changed') {
                    showToast(strings.drafts.staleTitle, 'info')
                } else if (outcome === 'failed') {
                    showToast(strings.drafts.cleanupFailed, 'info')
                }
                // Make the stale row visible (and deletable) in the drafts list either way.
                void queryClient.invalidateQueries({ queryKey: ['bskyDrafts', session?.did] })
            }
        },
        onError: () => {
            // Roll back the optimistic insert by refetching from the server
            tempUriRef.current = null
            void queryClient.invalidateQueries({ queryKey: ['posts'] })
            void queryClient.invalidateQueries({ queryKey: ['thread'] })
            void queryClient.invalidateQueries({ queryKey: ['ap-thread'] })
        },
    })

    const handleClose = useCallback(() => {
        if (post.postText.trim() || post.selectedImages.length > 0 || post.linkCard?.metadata) {
            setShowDiscardConfirm(true)
        } else {
            performCloseRef.current()
        }
    }, [post.postText, post.selectedImages.length, post.linkCard?.metadata])

    const handleCloseRef = useRef(handleClose)
    useEffect(() => {
        handleCloseRef.current = handleClose
    }, [handleClose])

    // Read inside the Escape handler below, which is installed once per isOpen effect run (not
    // on every editingAltTextId change) — a ref is required so the handler sees the current value
    // instead of the one captured when the listener was added.
    const editingAltTextIdRef = useRef(editingAltTextId)
    useEffect(() => {
        editingAltTextIdRef.current = editingAltTextId
    }, [editingAltTextId])

    const attachmentCount = post.selectedImages.length
    const hasAttachments = attachmentCount > 0
    const attachmentKind: 'photo' | 'video' = post.selectedImages.some(a => a.type === 'video')
        ? 'video'
        : 'photo'

    /**
     * Write the composer's state to the stash.
     *
     * Bluesky drafts do not store images or video (G1-c), and the composer closes on a
     * successful save — so the browser `File` objects are the user's only copy. The
     * `confirmedDiscardAttachments` gate makes the destructive confirmation the ONLY path that
     * can reach `createDraft`/`updateDraft` while attachments are present.
     */
    const performSaveDraft = useCallback(
        async (options?: { confirmedDiscardAttachments?: boolean }) => {
            if (post.selectedImages.length > 0 && !options?.confirmedDiscardAttachments) {
                return
            }

            // com.atproto.repo.strongRef requires a cid; QuoteContext.cid is optional (absent
            // for Mastodon quotes). Warn rather than emitting a fabricated cid.
            if (effectiveQuoteOf && !effectiveQuoteOf.cid) {
                showToast(strings.drafts.quoteNotSavedNoCid, 'info')
            }

            try {
                const id = await saveDraft({
                    input: {
                        text: post.postText,
                        // Persist the URL the link card actually resolved to, so a redirect the
                        // user explicitly accepted is what gets saved.
                        externalUri: post.linkCard?.metadata?.url ?? null,
                        quote:
                            effectiveQuoteOf?.uri && effectiveQuoteOf.cid
                                ? { uri: effectiveQuoteOf.uri, cid: effectiveQuoteOf.cid }
                                : null,
                    },
                    existingId: loadedDraftIdRef.current ?? undefined,
                    knownUpdatedAt: loadedDraftUpdatedAtRef.current ?? undefined,
                })
                // An imported copy becomes an ordinary Mackinac-owned draft from here on.
                loadedDraftIdRef.current = id
                setShowAttachmentSaveConfirm(false)
                setShowDiscardConfirm(false)
                performCloseRef.current()
            } catch (err) {
                setShowAttachmentSaveConfirm(false)
                if (err instanceof DraftChangedElsewhereError) {
                    setStaleConflict({ action: 'save' })
                    return
                }
                if (err instanceof DraftAlreadyDeletedError) {
                    // Drop the stale link so the next save creates a fresh draft.
                    loadedDraftIdRef.current = null
                    loadedDraftUpdatedAtRef.current = null
                    showToast(strings.drafts.alreadyDeleted, 'info')
                    return
                }
                if (err instanceof DraftTextTooLongError) {
                    const over = err.actual - err.max
                    showToast(
                        err.limit === 'characters'
                            ? strings.drafts.textTooLongCharacters(over)
                            : strings.drafts.textTooLongGraphemes(over),
                        'error'
                    )
                    return
                }
                if (isDraftLimitReachedError(err)) {
                    showToast(strings.drafts.limitReached, 'error')
                    return
                }
                console.error('[BaseComposeModal] saveDraft failed:', err)
                showToast(strings.drafts.saveFailed, 'error')
            }
        },
        [post.postText, post.selectedImages, post.linkCard, effectiveQuoteOf, saveDraft, showToast]
    )

    const handleSaveDraft = useCallback(() => performSaveDraft(), [performSaveDraft])

    const maxCharacters = isFediverse ? 500 : 300
    const remainingCharacters = maxCharacters - post.postText.length
    const isOverLimit = remainingCharacters < 0
    const isPixelfed = selectedProtocol === 'pixelfed'
    const hasRequiredMedia = !isPixelfed || post.selectedImages.length > 0
    // Safe: removeImage calls videoUpload.reset() which clears blobRef,
    // so a stale blobRef from a previous video cannot leak to a new attachment.
    const videoIsReady =
        !post.selectedImages.some(a => a.type === 'video') || post.videoUpload.phase === 'done'
    // Both draft-hydration contracts gate publishing until they settle — by success, by an
    // explicit dismissal, or by an explicit conflict choice. Publishing a draft that was meant
    // to carry a quote or a link card without it is a silent content change.
    const isDraftHydrationPending = quoteHydration !== null || post.draftLinkHydration !== null
    const canPost =
        (post.postText.trim() || post.selectedImages.length > 0) &&
        hasRequiredMedia &&
        !post.isPosting &&
        !isOverLimit &&
        videoIsReady &&
        !isDraftHydrationPending

    /**
     * Resolve a draft's bare `{uri, cid}` strongRef into a renderable Post.
     *
     * A `QuoteContext` is never constructed with a placeholder or partial post — the composer
     * renders `<QuotedPostEmbed post={...}/>` directly from it.
     */
    const hydrateQuote = useCallback(
        async (ref: BskyDraftRecordRef) => {
            if (!draftsAgent) return
            setQuoteHydration({ status: 'pending' })
            try {
                const quotedPost = await fetchEmbeddedPost(ref.uri, draftsAgent)
                if (!quotedPost.cid) {
                    // No cid at all is a failure, not a match.
                    setQuoteHydration({ status: 'error' })
                    return
                }
                if (quotedPost.cid !== ref.cid) {
                    // The record was edited after the draft was saved. Previewing one version
                    // while publishing another is a silent content change — require a choice.
                    setQuoteHydration({ status: 'conflict', post: quotedPost, ref })
                    return
                }
                setLoadedQuoteContext({ uri: ref.uri, cid: quotedPost.cid, post: quotedPost })
                setQuoteHydration(null)
            } catch {
                // Deleted, blocked, taken down, detached, or a network error.
                setQuoteHydration({ status: 'error' })
            }
        },
        [draftsAgent]
    )

    /**
     * Load a draft's supported content into the composer.
     *
     * `asImport` is the Lossless-Only Editing hinge: an imported copy leaves `loadedDraftIdRef`
     * null, so nothing is written until the user explicitly saves, and the source draft is never
     * updated or deleted.
     */
    const loadDraftIntoComposer = useCallback(
        (draft: BskyDraft, asImport: boolean) => {
            if (!isDraftsEnabled) return
            loadedDraftIdRef.current = asImport ? null : draft.id
            loadedDraftUpdatedAtRef.current = asImport ? null : draft.updatedAt
            post.setPostText(draft.text)
            setLoadedQuoteContext(null)
            setQuoteHydration(null)
            if (draft.recordRef) void hydrateQuote(draft.recordRef)
            // Bluesky drafts store only the bare URL, so metadata is re-fetched over the network.
            if (draft.externalUri) post.prefetchFromDraft(draft.externalUri)
            setShowDraftsList(false)
            setImportCandidate(null)
        },
        // post.setPostText (useState setter) and post.prefetchFromDraft (useCallback in
        // useLinkCard) are both referentially stable; `post` itself is a fresh object every
        // render, so adding it here would defeat this callback's memoization.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isDraftsEnabled, post.setPostText, post.prefetchFromDraft, hydrateQuote]
    )

    const handleOpenDraft = useCallback(
        (draft: BskyDraft) => {
            // Only Mackinac-created drafts still within the supported shape may be opened for
            // in-place editing; everything else goes through import-a-copy.
            if (!draft.classification.editable) return
            loadDraftIntoComposer(draft, false)
        },
        [loadDraftIntoComposer]
    )

    const handleDeleteDraft = useCallback(
        async (draft: BskyDraft) => {
            // Deletion carries none of the data-loss risk editing does — it's safe for any
            // draft regardless of origin or shape, matching the official Bluesky app.
            try {
                await deleteDraft(draft.id, draft.updatedAt)
            } catch (err) {
                if (err instanceof DraftChangedElsewhereError) {
                    setStaleConflict({ action: 'delete' })
                } else if (err instanceof DraftAlreadyDeletedError) {
                    showToast(strings.drafts.alreadyDeleted, 'info')
                } else {
                    console.error('[BaseComposeModal] deleteDraft failed:', err)
                    showToast(strings.drafts.deleteFailed, 'error')
                }
            }
        },
        [deleteDraft, showToast]
    )

    /** Re-fetch the list so classification is re-derived — the row may now be importable. */
    const handleReloadAfterConflict = useCallback(async () => {
        const action = staleConflict?.action
        setStaleConflict(null)
        if (action === 'save') {
            // The draft held by the composer is the stale one — drop the link so the user
            // re-opens it from the freshly classified list rather than saving over it.
            loadedDraftIdRef.current = null
            loadedDraftUpdatedAtRef.current = null
        }
        setShowDraftsList(true)
        await refetchDrafts()
    }, [refetchDrafts, staleConflict])

    const openDraftsList = useCallback(() => {
        setShowDraftsList(true)
        // Stash is shared state — always re-read on open rather than trusting the cache.
        void refetchDrafts()
    }, [refetchDrafts])

    useEffect(() => {
        if (!isOpen) return
        setIsClosing(false)
        setShowDiscardConfirm(false)
        setShowDraftsList(false)
        setImportCandidate(null)
        setDeleteCandidate(null)
        setShowAttachmentSaveConfirm(false)
        setQuoteHydration(null)
        setStaleConflict(null)
        setLoadedQuoteContext(null)
        setSelfLabel(undefined)
        setShowCWMenu(false)
        setDetectedLang(undefined)
        setUserLang(undefined)
        setLangCleared(false)
        setShowLangMenu(false)
        setEditingAltTextId(null)
        setAltTextDraft('')
        prevTextLengthRef.current = 0
        loadedDraftIdRef.current = null
        loadedDraftUpdatedAtRef.current = null
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return
            // Authoritative router: the alt text modal has no document-level Escape handler of
            // its own, so this is the only listener that ever sees the key. When it's open,
            // Escape must close only it — never fall through to the discard-post flow.
            if (editingAltTextIdRef.current !== null) {
                e.preventDefault()
                setEditingAltTextId(null)
                setAltTextDraft('')
                return
            }
            handleCloseRef.current()
        }
        void loadTinyld().catch(error => {
            console.error('[BaseComposeModal] loadTinyld failed:', error)
        })
        document.addEventListener('keydown', handleEscape)
        lock()
        return () => {
            document.removeEventListener('keydown', handleEscape)
            unlock()
            post.reset()
            setLoadedQuoteContext(null)
        }
        // post.reset (useCallback) and post.setPostText (useState setter) are referentially
        // stable; `post` itself is a fresh object every render, so this effect must run only
        // on isOpen changes, not on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, post.reset, post.setPostText])

    // Close alt text modal if the image being edited is removed
    useEffect(() => {
        if (editingAltTextId && !post.selectedImages.find(img => img.id === editingAltTextId)) {
            setEditingAltTextId(null)
            setAltTextDraft('')
        }
    }, [post.selectedImages, editingAltTextId])

    useEffect(() => {
        if (post.selectedImages.length === 0 && selfLabel) {
            setSelfLabel(undefined)
        }
    }, [post.selectedImages.length, selfLabel])

    // Auto-detect language as user types
    const { postText } = post
    useEffect(() => {
        const prev = prevTextLengthRef.current
        prevTextLengthRef.current = postText.length

        // A late result for older text, or one landing after unmount, is dropped.
        // A failed chunk load degrades to no auto-detected language.
        let cancelled = false
        const detect = () => {
            loadTinyld()
                .then(({ detectAll }) => {
                    if (cancelled) return
                    const results = detectAll(postText, { only: [...DETECTION_LANGS] })
                    setDetectedLang(results.length > 0 ? results[0].lang : undefined)
                })
                .catch(error => {
                    if (cancelled) return
                    console.error('[BaseComposeModal] loadTinyld failed:', error)
                })
        }

        if (prev < 20 && postText.length >= 20) {
            // Crossing threshold upward: reset cleared state and detect
            setLangCleared(false)
            detect()
            return () => {
                cancelled = true
            }
        }

        if (postText.length < 20) {
            setDetectedLang(undefined)
            return
        }

        if (langCleared) {
            if (Math.abs(postText.length - langClearedAtLengthRef.current) >= 10) {
                setLangCleared(false)
            }
            return
        }

        detect()
        return () => {
            cancelled = true
        }
    }, [postText, langCleared])

    if (!isOpen) return null

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-start justify-center p-0 sm:pt-[10vh] sm:p-4"
            style={isMobile ? { paddingBottom: keyboardHeight } : undefined}
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity hidden sm:block"
                onClick={handleClose}
            />

            {/* Modal */}
            <div
                role="dialog"
                className={`relative bg-surface-light dark:bg-surface-dark sm:border sm:border-border-light sm:dark:border-border-dark sm:rounded-2xl shadow-2xl w-full sm:max-w-lg sm:overflow-hidden ${isMobile ? (isClosing ? 'animate-slide-down' : 'animate-slide-up') : 'animate-in fade-in zoom-in-95 duration-200'} flex flex-col h-full sm:h-auto sm:min-h-[60vh] sm:max-h-[90vh]`}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border-light dark:border-border-dark">
                    <button
                        onClick={() => {
                            if (showDraftsList) {
                                backButtonClickedRef.current = true
                                setShowDraftsList(false)
                            } else {
                                handleClose()
                            }
                        }}
                        className="text-sm text-text-primary-light dark:text-text-primary-dark hover:opacity-70 transition-opacity"
                    >
                        {showDraftsList ? strings.common.back : 'Cancel'}
                    </button>
                    {showDraftsList && (
                        <p
                            ref={draftsHeadingRef}
                            role="heading"
                            aria-level={2}
                            tabIndex={-1}
                            className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark focus:outline-none"
                        >
                            {strings.drafts.title}
                        </p>
                    )}
                    <div className="flex items-center gap-2">
                        {/* Always shown when drafts are available — the list itself is only
                            fetched when the dialog opens, so the count is unknown until then. */}
                        {!showDraftsList && !!session && isDraftsEnabled && (
                            <button
                                ref={draftsButtonRef}
                                type="button"
                                onClick={() => openDraftsList()}
                                className="text-sm text-text-primary-light dark:text-text-primary-dark hover:opacity-70 transition-opacity"
                            >
                                {strings.drafts.openTitle}
                            </button>
                        )}
                        {!showDraftsList && (
                            <button
                                onClick={post.handlePost}
                                disabled={!canPost}
                                className="px-5 py-1.5 bg-primary text-white text-sm font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                            >
                                {post.isPosting ? 'Posting...' : submitLabel}
                            </button>
                        )}
                    </div>
                </div>

                {/* Scrollable content area */}
                <div className="overflow-y-auto flex-1 min-h-0">
                    {showDraftsList && (
                        <>
                            {isLoadingDrafts && (
                                <p className="px-4 py-3 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {strings.drafts.loading}
                                </p>
                            )}
                            {isDraftsError && !isLoadingDrafts && (
                                <p className="px-4 py-3 text-sm text-red-500">
                                    {strings.drafts.loadError}
                                </p>
                            )}
                            {!isLoadingDrafts && !isDraftsError && drafts.length === 0 && (
                                <p className="px-4 py-3 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {strings.drafts.empty}
                                </p>
                            )}
                            {drafts.map(draft => {
                                const { editable, droppedFeatures } = draft.classification
                                const reasons = lossReasons(droppedFeatures)
                                const tags = draftMetadataTags(draft)
                                return (
                                    <div
                                        key={draft.id}
                                        data-testid="draft-row"
                                        data-editable={editable}
                                        className="px-4 py-3 border-b border-border-light dark:border-border-dark last:border-b-0"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-text-primary-light dark:text-text-primary-dark line-clamp-6">
                                                    {draft.text || (
                                                        <span className="italic text-text-secondary-light dark:text-text-secondary-dark">
                                                            {strings.drafts.noText}
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1">
                                                    {formatRelativeTime(draft.updatedAt)}
                                                </p>
                                            </div>
                                        </div>
                                        {tags.length > 0 && (
                                            <div
                                                data-testid="draft-tags"
                                                className="mt-1 flex flex-col gap-0.5"
                                            >
                                                {tags.map(({ key, Icon, text }) => (
                                                    <span
                                                        key={key}
                                                        className="flex items-center gap-1 text-xs text-text-secondary-light dark:text-text-secondary-dark"
                                                    >
                                                        <Icon className="w-3 h-3 flex-shrink-0" />
                                                        {text}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {!editable && reasons.length > 0 && (
                                            <ul className="mt-1 text-xs text-text-secondary-light dark:text-text-secondary-dark list-disc list-inside">
                                                {reasons.map(reason => (
                                                    <li key={reason}>{reason}</li>
                                                ))}
                                            </ul>
                                        )}
                                        {!editable && reasons.length === 0 && (
                                            <p className="mt-1 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                                {strings.drafts.readOnlyReason}
                                            </p>
                                        )}
                                        <div className="mt-2 flex items-center gap-4">
                                            {editable ? (
                                                <button
                                                    onClick={() => handleOpenDraft(draft)}
                                                    className="text-sm font-semibold text-primary hover:opacity-70 transition-opacity"
                                                >
                                                    {strings.drafts.open}
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => setImportCandidate(draft)}
                                                    className="text-sm font-semibold text-primary hover:opacity-70 transition-opacity"
                                                >
                                                    {strings.drafts.importCopy}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setDeleteCandidate(draft)}
                                                className="text-sm font-semibold text-red-500 hover:opacity-70 transition-opacity"
                                            >
                                                {strings.drafts.delete}
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                            {/* Explicit paging only — no infinite scroll, which would
                                multiply hydration fetches for no user benefit. */}
                            {hasMoreDrafts && (
                                <button
                                    onClick={() => void fetchMoreDrafts()}
                                    disabled={isFetchingMoreDrafts}
                                    className="w-full py-3 text-sm font-semibold text-primary hover:bg-black/5 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
                                >
                                    {isFetchingMoreDrafts
                                        ? strings.common.loadingMore
                                        : strings.drafts.loadMore}
                                </button>
                            )}
                        </>
                    )}
                    {/*
                      The composer subtree (RichComposer and everything around it) must stay
                      mounted for the modal's whole lifetime — only visibility toggles here.
                      RichComposer runs a ONE-TIME migration effect on mount to clean up legacy
                      mention syntax; unmounting/remounting it on every Drafts open/close would
                      rerun that effect and silently mangle in-progress text containing the
                      literal pattern `@[display](id)`.
                    */}
                    <div style={showDraftsList ? { display: 'none' } : undefined}>
                        {/* Content slot BEFORE compose (replies — parent post above input) */}
                        {!quoteOf && children}

                        {/* Compose area */}
                        <div className="px-4 pt-3 pb-2">
                            <div className="flex gap-3">
                                <Avatar
                                    src={currentUserAvatar}
                                    alt={strings.compose.youAvatarAlt}
                                    size="sm"
                                />
                                <div
                                    ref={composerContainerRef}
                                    className="min-w-0 flex-1 flex flex-col"
                                >
                                    <RichComposer
                                        value={post.postText}
                                        onChange={post.setPostText}
                                        placeholder={placeholder}
                                        protocol={selectedProtocol}
                                        borderless
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Link Card Preview */}
                        {selectedProtocol === 'bluesky' &&
                            post.linkCard &&
                            post.selectedImages.length === 0 && (
                                <div className="px-4 pb-2">
                                    <div className="ml-11">
                                        <LinkCardPreview
                                            metadata={post.linkCard.metadata}
                                            isLoading={post.linkCard.isLoading}
                                            onDismiss={post.dismissLinkCard}
                                        />
                                    </div>
                                </div>
                            )}

                        {/* Link-card hydration from a draft: failure and redirect-mismatch choices */}
                        {post.draftLinkHydration && (
                            <div className="px-4 pb-2">
                                <div
                                    className="ml-11 rounded-lg border border-border-light dark:border-border-dark p-3"
                                    data-testid="draft-link-hydration"
                                >
                                    {post.draftLinkHydration.status === 'pending' ? (
                                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                            {strings.drafts.linkLoading}
                                        </p>
                                    ) : (
                                        <>
                                            <p className="text-sm text-text-primary-light dark:text-text-primary-dark">
                                                {post.draftLinkHydration.status === 'mismatch'
                                                    ? strings.drafts.linkRedirectTitle
                                                    : strings.drafts.linkFailed}
                                            </p>
                                            {post.draftLinkHydration.status === 'mismatch' &&
                                                post.draftLinkHydration.resolvedUrl && (
                                                    <p className="mt-1 break-all text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                                        {strings.drafts.linkRedirectBody(
                                                            post.draftLinkHydration.resolvedUrl
                                                        )}
                                                    </p>
                                                )}
                                            <div className="mt-2 flex flex-wrap gap-3">
                                                {post.draftLinkHydration.status === 'mismatch' && (
                                                    <button
                                                        type="button"
                                                        onClick={post.acceptResolvedDraftLink}
                                                        className="text-sm font-semibold text-primary hover:opacity-70 transition-opacity"
                                                    >
                                                        {strings.drafts.linkUseResolved}
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={post.dismissDraftLink}
                                                    className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark hover:opacity-70 transition-opacity"
                                                >
                                                    {strings.drafts.linkLoadWithout}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => performCloseRef.current()}
                                                    className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark hover:opacity-70 transition-opacity"
                                                >
                                                    {strings.drafts.linkCancel}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Media Previews */}
                        {post.selectedImages.length > 0 && (
                            <div className="px-4 pb-2">
                                <div className="grid grid-cols-2 gap-2 ml-11">
                                    {post.selectedImages.map(attachment => (
                                        <div key={attachment.id} className="relative group">
                                            {attachment.type === 'video' &&
                                            (post.videoUpload.phase === 'uploading' ||
                                                post.videoUpload.phase === 'processing') ? (
                                                <VideoUploadProgress
                                                    phase={post.videoUpload.phase}
                                                    progress={post.videoUpload.progress}
                                                    onRemove={() => post.removeImage(attachment.id)}
                                                    aspectRatio={attachment.aspectRatio}
                                                />
                                            ) : attachment.type === 'video' &&
                                              post.videoUpload.phase === 'error' ? (
                                                <div>
                                                    <video
                                                        src={attachment.preview}
                                                        className="w-full h-48 object-cover rounded-lg border-2 border-red-500"
                                                        muted
                                                        playsInline
                                                        preload="metadata"
                                                    />
                                                    <p className="mt-1 text-xs text-red-500 px-1 ml-11">
                                                        {post.videoUpload.error}
                                                    </p>
                                                </div>
                                            ) : attachment.type === 'video' ? (
                                                <video
                                                    src={attachment.preview}
                                                    className="w-full h-48 object-contain rounded-lg border border-border-light dark:border-border-dark bg-black"
                                                    controls
                                                    autoPlay
                                                    loop
                                                    muted
                                                    playsInline
                                                />
                                            ) : (
                                                <img
                                                    src={attachment.preview}
                                                    alt={
                                                        attachment.altText ||
                                                        'Image attachment preview'
                                                    }
                                                    className="w-full h-48 object-cover rounded-lg border border-border-light dark:border-border-dark"
                                                />
                                            )}
                                            <button
                                                type="button"
                                                aria-label={
                                                    attachment.altText
                                                        ? 'Edit alt text'
                                                        : 'Add alt text'
                                                }
                                                onClick={() => {
                                                    setEditingAltTextId(attachment.id)
                                                    setAltTextDraft(attachment.altText)
                                                }}
                                                className={`absolute top-2 left-2 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-bold transition-colors ${attachment.altText ? 'bg-primary text-white' : 'bg-black/70 hover:bg-black/90 text-white'}`}
                                            >
                                                <Plus className="w-3 h-3" aria-hidden="true" />
                                                ALT
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => post.removeImage(attachment.id)}
                                                className="absolute top-2 right-2 p-1 bg-black/70 hover:bg-black/90 rounded-full transition-colors"
                                            >
                                                <X className="w-4 h-4 text-white" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {/* Content slot AFTER compose (quotes — quoted post below input) */}
                        {quoteOf && children}

                        {/* Quoted post preview restored from a saved draft (no quoteOf prop) */}
                        {!quoteOf && loadedQuoteContext && (
                            <div className="px-4 pt-3 pb-1 ml-11">
                                <QuotedPostEmbed post={loadedQuoteContext.post} />
                            </div>
                        )}

                        {/* Quote hydration from a draft: loading, failure, and CID-mismatch choices */}
                        {!quoteOf && quoteHydration && (
                            <div className="px-4 pt-3 pb-1">
                                <div
                                    className="ml-11 rounded-lg border border-border-light dark:border-border-dark p-3"
                                    data-testid="draft-quote-hydration"
                                >
                                    {quoteHydration.status === 'pending' ? (
                                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                            {strings.drafts.quoteLoading}
                                        </p>
                                    ) : (
                                        <>
                                            <p className="text-sm text-text-primary-light dark:text-text-primary-dark">
                                                {quoteHydration.status === 'conflict'
                                                    ? strings.drafts.quoteConflictTitle
                                                    : strings.drafts.quoteFailed}
                                            </p>
                                            <p className="mt-1 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                                {quoteHydration.status === 'conflict'
                                                    ? strings.drafts.quoteConflictBody
                                                    : strings.drafts.quoteFailedDeleted}
                                            </p>
                                            <div className="mt-2 flex flex-wrap gap-3">
                                                {quoteHydration.status === 'conflict' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            // Adopt the hydrated cid; it is persisted
                                                            // on the next save, never silently.
                                                            setLoadedQuoteContext({
                                                                uri: quoteHydration.ref.uri,
                                                                cid: quoteHydration.post.cid,
                                                                post: quoteHydration.post,
                                                            })
                                                            setQuoteHydration(null)
                                                        }}
                                                        className="text-sm font-semibold text-primary hover:opacity-70 transition-opacity"
                                                    >
                                                        {strings.drafts.quoteUseLatest}
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setLoadedQuoteContext(null)
                                                        setQuoteHydration(null)
                                                    }}
                                                    className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark hover:opacity-70 transition-opacity"
                                                >
                                                    {strings.drafts.quoteLoadWithout}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => performCloseRef.current()}
                                                    className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark hover:opacity-70 transition-opacity"
                                                >
                                                    {strings.drafts.quoteCancel}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Bottom toolbar */}
                {!showDraftsList && (
                    <div className="flex items-center justify-between px-4 py-2 pb-[max(8px,env(safe-area-inset-bottom))] border-t border-border-light dark:border-border-dark">
                        <div className="flex items-center gap-1">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,video/mp4,video/quicktime"
                                multiple={!post.selectedImages.some(a => a.type === 'video')}
                                onChange={post.handleImageSelect}
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={
                                    post.selectedImages.length >= 4 ||
                                    post.selectedImages.some(a => a.type === 'video')
                                }
                                className={`p-2 text-primary hover:bg-primary/10 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isPixelfed && post.selectedImages.length === 0 ? 'ring-2 ring-primary/40 bg-primary/10' : ''}`}
                                title="Add images or video"
                            >
                                <Image className="w-5 h-5" />
                            </button>
                            {isPixelfed && post.selectedImages.length === 0 && (
                                <span className="text-xs text-primary font-medium">
                                    Photo required
                                </span>
                            )}
                            {/* Content warning */}
                            {post.selectedImages.length > 0 &&
                                (selfLabel ? (
                                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium">
                                        <ShieldAlert className="w-3 h-3" />
                                        <span>
                                            {CW_LABELS.find(l => l.val === selfLabel)?.label}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setSelfLabel(undefined)}
                                            className="hover:opacity-70"
                                            title={strings.compose.removeContentWarningTitle}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ) : (
                                    <div ref={cwMenuRef} className="relative">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowCWMenu(prev => !prev)
                                                setShowLangMenu(false)
                                            }}
                                            className="p-2 text-text-secondary-light dark:text-text-secondary-dark hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-colors"
                                            title={strings.compose.contentWarningTitle}
                                        >
                                            <ShieldAlert className="w-5 h-5" />
                                        </button>
                                        {showCWMenu && (
                                            <div className="absolute bottom-full left-0 mb-1 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg shadow-lg overflow-hidden z-10 min-w-36">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelfLabel(undefined)
                                                        setShowCWMenu(false)
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                                >
                                                    {strings.compose.cwNone}
                                                </button>
                                                {CW_LABELS.map(opt => (
                                                    <button
                                                        key={opt.val}
                                                        type="button"
                                                        onClick={() => {
                                                            setSelfLabel(opt.val)
                                                            setShowCWMenu(false)
                                                        }}
                                                        className="block w-full text-left px-4 py-2 text-sm text-text-primary-light dark:text-text-primary-dark hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            {/* Language control */}
                            {effectiveLang ? (
                                <div
                                    ref={langMenuRef}
                                    className="relative flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium"
                                >
                                    <Globe className="w-3 h-3" />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowLangMenu(prev => !prev)
                                            setShowCWMenu(false)
                                        }}
                                        className="font-mono font-bold"
                                        title={strings.compose.changeLanguageTitle}
                                    >
                                        {effectiveLang.toUpperCase()}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            langClearedAtLengthRef.current = postText.length
                                            setLangCleared(true)
                                            setDetectedLang(undefined)
                                            setUserLang(undefined)
                                        }}
                                        className="hover:opacity-70"
                                        title={strings.compose.removeLanguageTitle}
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                    {showLangMenu && (
                                        <div className="absolute bottom-full left-0 mb-1 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg shadow-lg overflow-y-auto z-10 min-w-36 max-h-48">
                                            {COMMON_LANGS.map(lang => (
                                                <button
                                                    key={lang.code}
                                                    type="button"
                                                    onClick={() => {
                                                        setUserLang(lang.code)
                                                        setLangCleared(false)
                                                        setShowLangMenu(false)
                                                    }}
                                                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${lang.code === effectiveLang ? 'text-primary font-semibold' : 'text-text-primary-light dark:text-text-primary-dark'}`}
                                                >
                                                    {lang.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div ref={langMenuRef} className="relative">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowLangMenu(prev => !prev)
                                            setShowCWMenu(false)
                                        }}
                                        className="p-2 text-text-secondary-light dark:text-text-secondary-dark hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-colors"
                                        title={strings.compose.setLanguageTitle}
                                    >
                                        <Globe className="w-5 h-5" />
                                    </button>
                                    {showLangMenu && (
                                        <div className="absolute bottom-full left-0 mb-1 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg shadow-lg overflow-y-auto z-10 min-w-36 max-h-48">
                                            {COMMON_LANGS.map(lang => (
                                                <button
                                                    key={lang.code}
                                                    type="button"
                                                    onClick={() => {
                                                        setUserLang(lang.code)
                                                        setLangCleared(false)
                                                        setShowLangMenu(false)
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-text-primary-light dark:text-text-primary-dark hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                                >
                                                    {lang.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <span
                            className={`text-sm ${isOverLimit ? 'text-red-500 font-semibold' : remainingCharacters < 50 ? 'text-yellow-500' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                        >
                            {remainingCharacters}
                        </span>
                    </div>
                )}
            </div>

            {/* Alt text modal */}
            {editingAltTextId && post.selectedImages.find(img => img.id === editingAltTextId) && (
                <AltTextModal
                    imagePreview={
                        post.selectedImages.find(img => img.id === editingAltTextId)!.preview
                    }
                    draft={altTextDraft}
                    onDraftChange={setAltTextDraft}
                    onSave={() => {
                        post.updateImageAltText(editingAltTextId, altTextDraft)
                        setEditingAltTextId(null)
                    }}
                    onClose={() => setEditingAltTextId(null)}
                />
            )}

            {/* Discard confirmation */}
            {showDiscardConfirm && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-[60]"
                        onClick={() => setShowDiscardConfirm(false)}
                    />
                    <div className="fixed inset-0 z-[61] flex items-center justify-center p-6">
                        <div className="bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden">
                            <div className="px-4 pt-5 pb-3 text-center">
                                <p className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                                    {isDraftsEnabled && !!session
                                        ? strings.drafts.savePrompt
                                        : 'Discard post?'}
                                </p>
                                <p className="mt-1 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {isDraftsEnabled && !!session
                                        ? strings.drafts.savePromptSubtitle
                                        : 'Your changes will be lost.'}
                                </p>
                            </div>
                            <div className="flex flex-col border-t border-border-light dark:border-border-dark">
                                {!!session && isDraftsEnabled && (
                                    <>
                                        <button
                                            onClick={handleSaveDraft}
                                            disabled={isSaving || hasAttachments}
                                            className="w-full py-3 text-sm font-semibold text-primary hover:bg-black/5 dark:hover:bg-white/5 transition-colors border-b border-border-light dark:border-border-dark disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isSaving
                                                ? strings.drafts.saving
                                                : strings.drafts.saveDraft}
                                        </button>
                                        {hasAttachments && (
                                            <>
                                                <p className="px-4 pb-2 text-center text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                                    {strings.drafts.attachmentsBlockSave(
                                                        attachmentCount,
                                                        attachmentKind
                                                    )}
                                                </p>
                                                {/*
                                                  A disabled control cannot open a dialog, so the
                                                  destructive path is a SEPARATE, always-enabled
                                                  action. This is the only affordance that can
                                                  reach createDraft/updateDraft while the user's
                                                  only copy of the media lives in composer state.
                                                */}
                                                <button
                                                    onClick={() =>
                                                        setShowAttachmentSaveConfirm(true)
                                                    }
                                                    className="w-full py-3 text-sm font-semibold text-primary hover:bg-black/5 dark:hover:bg-white/5 transition-colors border-b border-border-light dark:border-border-dark"
                                                >
                                                    {strings.drafts.saveWithoutAttachments(
                                                        attachmentCount,
                                                        attachmentKind
                                                    )}
                                                </button>
                                            </>
                                        )}
                                    </>
                                )}
                                <div className="flex">
                                    <button
                                        onClick={() => setShowDiscardConfirm(false)}
                                        className="flex-1 py-3 text-sm font-medium text-text-primary-light dark:text-text-primary-dark hover:bg-black/5 dark:hover:bg-white/5 transition-colors border-r border-border-light dark:border-border-dark"
                                    >
                                        {strings.compose.keepEditing}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowDiscardConfirm(false)
                                            performCloseRef.current()
                                        }}
                                        className="flex-1 py-3 text-sm font-semibold text-red-500 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                    >
                                        {strings.compose.discard}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/*
              Destructive confirmation for saving a draft while attachments are present.
              This is the ONLY path that may reach createDraft/updateDraft in that state.
            */}
            {showAttachmentSaveConfirm && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-[62]"
                        onClick={() => setShowAttachmentSaveConfirm(false)}
                    />
                    <div className="fixed inset-0 z-[63] flex items-center justify-center p-6">
                        <div
                            className="bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden"
                            data-testid="attachment-save-confirm"
                        >
                            <div className="px-4 pt-5 pb-3 text-center">
                                <p className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                                    {strings.drafts.discardAttachmentsTitle(
                                        attachmentCount,
                                        attachmentKind
                                    )}
                                </p>
                                <p className="mt-1 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {strings.drafts.discardAttachmentsBody(
                                        attachmentCount,
                                        attachmentKind
                                    )}
                                </p>
                            </div>
                            <div className="flex border-t border-border-light dark:border-border-dark">
                                <button
                                    onClick={() => setShowAttachmentSaveConfirm(false)}
                                    className="flex-1 py-3 text-sm font-medium text-text-primary-light dark:text-text-primary-dark hover:bg-black/5 dark:hover:bg-white/5 transition-colors border-r border-border-light dark:border-border-dark"
                                >
                                    {strings.drafts.discardAttachmentsCancel}
                                </button>
                                <button
                                    onClick={() =>
                                        void performSaveDraft({
                                            confirmedDiscardAttachments: true,
                                        })
                                    }
                                    disabled={isSaving}
                                    className="flex-1 py-3 text-sm font-semibold text-red-500 hover:bg-black/5 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
                                >
                                    {strings.drafts.discardAttachmentsConfirm}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/*
              Import-a-copy confirmation. Cancelling here is a complete no-op — no network write
              of any kind — so a user who backs out is never left with an abandoned clone eating
              their stash quota. The clone is created only on an explicit later save.
            */}
            {importCandidate && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-[62]"
                        onClick={() => setImportCandidate(null)}
                    />
                    <div className="fixed inset-0 z-[63] flex items-center justify-center p-6">
                        <div
                            className="bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden"
                            data-testid="import-confirm"
                        >
                            <div className="px-4 pt-5 pb-3">
                                <p className="text-base font-semibold text-center text-text-primary-light dark:text-text-primary-dark">
                                    {strings.drafts.importTitle}
                                </p>
                                <p className="mt-1 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {strings.drafts.importBody}
                                </p>
                                <ul className="mt-2 text-sm text-text-secondary-light dark:text-text-secondary-dark list-disc list-inside">
                                    {lossReasons(importCandidate.classification.droppedFeatures)
                                        .length > 0 ? (
                                        lossReasons(
                                            importCandidate.classification.droppedFeatures
                                        ).map(reason => <li key={reason}>{reason}</li>)
                                    ) : (
                                        <li>{strings.drafts.readOnlyReason}</li>
                                    )}
                                </ul>
                            </div>
                            <div className="flex border-t border-border-light dark:border-border-dark">
                                <button
                                    onClick={() => setImportCandidate(null)}
                                    className="flex-1 py-3 text-sm font-medium text-text-primary-light dark:text-text-primary-dark hover:bg-black/5 dark:hover:bg-white/5 transition-colors border-r border-border-light dark:border-border-dark"
                                >
                                    {strings.drafts.importCancel}
                                </button>
                                <button
                                    onClick={() => loadDraftIntoComposer(importCandidate, true)}
                                    className="flex-1 py-3 text-sm font-semibold text-primary hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                >
                                    {strings.drafts.importConfirm}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/*
              "Updated somewhere else" — the pre-mutation freshness check found a newer
              updatedAt, so the mutation was refused rather than clobbering another client's
              edit. See the accepted-TOCTOU note in bskyDraftsService.
            */}
            {staleConflict && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-[62]"
                        onClick={() => setStaleConflict(null)}
                    />
                    <div className="fixed inset-0 z-[63] flex items-center justify-center p-6">
                        <div
                            className="bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden"
                            data-testid="stale-draft-conflict"
                        >
                            <div className="px-4 pt-5 pb-3 text-center">
                                <p className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                                    {strings.drafts.staleTitle}
                                </p>
                                <p className="mt-1 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {strings.drafts.staleBody}
                                </p>
                            </div>
                            <div className="flex border-t border-border-light dark:border-border-dark">
                                <button
                                    onClick={() => setStaleConflict(null)}
                                    className="flex-1 py-3 text-sm font-medium text-text-primary-light dark:text-text-primary-dark hover:bg-black/5 dark:hover:bg-white/5 transition-colors border-r border-border-light dark:border-border-dark"
                                >
                                    {strings.drafts.staleCancel}
                                </button>
                                <button
                                    onClick={() => void handleReloadAfterConflict()}
                                    className="flex-1 py-3 text-sm font-semibold text-primary hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                >
                                    {strings.drafts.staleReload}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/*
              Delete confirmation. Cancelling is a complete no-op — handleDeleteDraft is only
              ever reached from the Discard button below.
            */}
            {deleteCandidate && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-[62]"
                        onClick={() => setDeleteCandidate(null)}
                    />
                    <div className="fixed inset-0 z-[63] flex items-center justify-center p-6">
                        <div
                            className="bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden"
                            data-testid="delete-draft-confirm"
                        >
                            <div className="px-4 pt-5 pb-3 text-center">
                                <p className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                                    {strings.drafts.deleteConfirmTitle}
                                </p>
                                <p className="mt-1 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                    {strings.drafts.deleteConfirmBody}
                                </p>
                                {!deleteCandidate.classification.editable && (
                                    <p className="mt-1 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                        {strings.drafts.deleteConfirmUnsupportedWarning}
                                    </p>
                                )}
                            </div>
                            <div className="flex border-t border-border-light dark:border-border-dark">
                                <button
                                    onClick={() => setDeleteCandidate(null)}
                                    className="flex-1 py-3 text-sm font-medium text-text-primary-light dark:text-text-primary-dark hover:bg-black/5 dark:hover:bg-white/5 transition-colors border-r border-border-light dark:border-border-dark"
                                >
                                    {strings.drafts.deleteConfirmCancel}
                                </button>
                                <button
                                    onClick={() => {
                                        const draft = deleteCandidate
                                        setDeleteCandidate(null)
                                        void handleDeleteDraft(draft)
                                    }}
                                    className="flex-1 py-3 text-sm font-semibold text-red-500 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                >
                                    {strings.drafts.deleteConfirmConfirm}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>,
        document.body
    )
}
